import { z } from 'zod';
import { definePath } from '../../router/definePath';
import { relativePathSchema } from '../../router/relativePath';
import { isUserCode, normalizeUserCode } from './deviceFlow';

// Emailed JWT (WWP-731); `.catch('')` avoids throwing on a malformed/coerced value.
const resetPasswordSearch = z.object({ token: z.jwt().optional().catch('').default('') });

const accountLockedSearch = z.object({
  reason: z.enum(['contract-terminated', 'locked']).catch('locked'),
});

// Post-auth target: one visit, so the URL and not the journey snapshot, which outlives it.
// `looseObject` keeps the entry's product/attribution params for the journey capture.
const returnToSearch = z.looseObject({ to: relativePathSchema.optional().catch(undefined) });

// `catch` rather than a hard failure: a mangled code lands on the in-page entry state, not a 404.
// Extends `returnToSearch` so a `?to=` target and its attribution params are not stripped here.
const tvSigninSearch = returnToSearch.extend({
  user_code: z.string().transform(normalizeUserCode).refine(isUserCode).optional().catch(undefined),
});

// Extends `returnToSearch` (not a plain object) so `to` and any journey/attribution params riding
// with it survive the hard redirect from `useNativeRegisterExit` instead of being stripped by a
// stricter schema. `?context=` is not declared here: the page beforeLoad unpacks that envelope into
// plain params before any route reads search (see `router/searchContext`).
// Signed JWT (WWP-731); `.catch(undefined)` avoids throwing on a malformed value.
const confirmEmailSearch = returnToSearch.extend({ token: z.jwt().optional().catch(undefined) });

export const LoginPaths = {
  redirect: definePath('public', { de: '/auth/redirect', en: '/auth/redirect' }),
  login: definePath('bare', { de: '/kunden-login', en: '/signin' }, { search: returnToSearch }),
  /** Identifier-first entry: serves sign-in and sign-up, hence not a "register" path. Its segment prefixes every journey step. */
  register: definePath('public', { de: '/konto/anmelden', en: '/account/signin' }, { search: returnToSearch }),
  /** `passwort`, not `konto`: the step sets only a password, and it avoids the `/konto/konto` stutter. */
  registerAccount: definePath('public', { de: '/konto/anmelden/passwort', en: '/account/signin/password' }, { search: returnToSearch }),
  registerProfile: definePath('public', { de: '/konto/anmelden/profil', en: '/account/signin/profile' }, { search: returnToSearch }),
  forgotPassword: definePath('public', { de: '/passwort-vergessen', en: '/forgot-password' }),
  resetPassword: definePath('public', { de: '/passwort-zuruecksetzen', en: '/reset-password' }, { search: resetPasswordSearch }),
  logoutConfirmation: definePath('bare', { de: '/abgemeldet', en: '/logged-out' }, { search: returnToSearch }),
  accountLocked: definePath('public', { de: '/konto-gesperrt', en: '/account-locked' }, { search: accountLockedSearch }),
  completeRegistration: definePath(
    'public',
    { de: '/konto/anmelden/vervollstaendigen', en: '/account/signin/complete' },
    { search: returnToSearch },
  ),
  linkApple: definePath('public', { de: '/konto/anmelden/apple-verknuepfen', en: '/account/signin/link-apple' }),
  /** Public — the token alone is proof, no session required. Kept at its original path. */
  confirmEmail: definePath('public', { de: '/konto/email-bestaetigen', en: '/account/confirm-email' }, { search: confirmEmailSearch }),
  /** Same redemption, registration flow: journey frame instead of the hub shell. */
  registerConfirmEmail: definePath(
    'public',
    { de: '/konto/anmelden/email-bestaetigen', en: '/account/signin/confirm-email' },
    { search: confirmEmailSearch },
  ),
  /** Device-flow approval. `authenticated`: the approval binds a device to an identity. */
  tvSignin: definePath('authenticated', { de: '/tv-anmelden', en: '/tv-signin' }, { search: tvSigninSearch }),
} as const;
